import StrategyRefChip from "@/components/strategy/StrategyRefChip";
import StrategyStatusChip from "@/components/strategy/StrategyStatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, X, Zap, Bot } from "lucide-react";

/**
 * GoLiveModal — confirmation flow to flip a strategy from PAPER → LIVE.
 *
 * Props:
 *   state          { strategy, broker, qty, daily, notional, typed }
 *   onClose        () => void
 *   onChange       (next) => void   // controlled — parent owns state
 *   onSubmit       () => Promise<void>
 *   submitting     bool
 */
export default function GoLiveModal({ state, onClose, onChange, onSubmit, submitting }) {
  if (!state) return null;
  const set = (patch) => onChange({ ...state, ...patch });

  return (
    <div
      data-testid="strategy-live-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="panel max-w-md w-full p-6 border border-red-500/40"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-section">GO LIVE — REAL MONEY</span>
          </div>
          <button
            data-testid="strategy-live-modal-close"
            onClick={onClose}
            className="txt-muted hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-xs txt-secondary mb-4">
          Strategy <span className="text-white">{state.strategy.name}</span>{" "}
          <StrategyRefChip id={state.strategy.id} testid="strategy-live-modal-ref" />{" "}
          <StrategyStatusChip src={state.strategy} testid="strategy-live-modal-status" /> will route real
          orders to your broker every minute when its entry rule fires. Hard guardrails below
          are enforced on every tick.
        </div>
        <div className="space-y-3">
          <div>
            <Label className="overline">Broker</Label>
            <select
              data-testid="strategy-live-broker"
              value={state.broker}
              onChange={(e) => set({ broker: e.target.value })}
              className="w-full bg-[var(--bg-page)] border border-[var(--border)] p-2 text-sm rounded-none"
            >
              <option value="zerodha">Zerodha</option>
              <option value="upstox">Upstox</option>
              <option value="dhan">Dhan</option>
              <option value="icici">ICICI</option>
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="overline">Max qty / order</Label>
              <Input
                data-testid="strategy-live-max-qty"
                type="number"
                min="1"
                max="1000"
                value={state.qty}
                onChange={(e) => set({ qty: e.target.value })}
                className="rounded-none"
              />
            </div>
            <div>
              <Label className="overline">Orders / day</Label>
              <Input
                data-testid="strategy-live-max-daily"
                type="number"
                min="1"
                max="50"
                value={state.daily}
                onChange={(e) => set({ daily: e.target.value })}
                className="rounded-none"
              />
            </div>
            <div>
              <Label className="overline">Notional cap ₹</Label>
              <Input
                data-testid="strategy-live-notional"
                type="number"
                min="100"
                max="500000"
                value={state.notional}
                onChange={(e) => set({ notional: e.target.value })}
                className="rounded-none"
              />
            </div>
          </div>
          {/* Autopilot toggle */}
          <div className={`flex items-center justify-between p-3 border ${state.autopilot ? "border-amber-500/40 bg-amber-500/5" : "border-[var(--border)]"}`}>
            <div className="flex items-center gap-2">
              <Bot className={`w-4 h-4 ${state.autopilot ? "text-amber-400" : "txt-muted"}`} />
              <div>
                <div className={`text-xs font-section ${state.autopilot ? "text-amber-400" : "txt-secondary"}`}>AUTOPILOT</div>
                <div className="text-[10px] txt-muted">Auto-execute orders when a signal fires (≥75% confidence)</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => set({ autopilot: !state.autopilot })}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${state.autopilot ? "bg-amber-400" : "bg-zinc-700"}`}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-black shadow-lg transform transition-transform ${state.autopilot ? "translate-x-4" : "translate-x-0"}`} />
            </button>
          </div>
          <div>
            <Label className="overline">Type DEPLOY LIVE to confirm</Label>
            <Input
              data-testid="strategy-live-typed-confirm"
              value={state.typed}
              onChange={(e) => set({ typed: e.target.value })}
              className="rounded-none font-mono"
              placeholder="DEPLOY LIVE"
            />
          </div>
          <Button
            data-testid="strategy-live-submit"
            onClick={onSubmit}
            disabled={submitting || state.typed !== "DEPLOY LIVE"}
            className="w-full rounded-none bg-red-500 hover:bg-red-600 text-white"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            DEPLOY LIVE
          </Button>
        </div>
      </div>
    </div>
  );
}
